import { Sensor } from "@core/apis/shared";

/**
 * Gets a friendly name for a sensor by combining street address with apartment ID
 */
export const getFriendlySensorName = (sensor: Sensor): string => {
  const streetAddress = sensor.street_address || "";

  if (!sensor.apartment) {
    return streetAddress;
  }

  // H + floor + apartment number
  const prefix = sensor.apartment.charAt(0);
  const aptNum = sensor.apartment.substring(1);
  const fullAptId = `${prefix}${(sensor.floor || "").padStart(2, "0")}${aptNum.padStart(2, "0")}`;

  return `${streetAddress} - ${fullAptId}`;
};

/**
 * Calculates the average of an array of values, excluding NaN and null values
 * @param values Array of numeric values
 * @returns Average of valid values, or NaN if no valid values
 */
function isValidNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

export function calculateAverage(values: (number | null | undefined)[]): number {
  const validValues = values.filter((v): v is number => isValidNumber(v));
  return validValues.length > 0
    ? validValues.reduce((sum, v) => sum + v, 0) / validValues.length
    : NaN;
}

/**
 * Gets the last valid non-null value from an array
 * @param values Array of numeric values
 * @returns The last valid value, or NaN if no valid values
 */
export function getLastValidValue(values: (number | null | undefined)[]): number {
  for (let i = values.length - 1; i >= 0; i--) {
    const value = values[i];
    if (isValidNumber(value)) return value;
  }
  return NaN;
}

/**
 * Sanitizes a sensor value, converting missing data to NaN
 * @param value The sensor reading value
 * @returns The value if valid, or NaN if missing/invalid
 */
export function sanitizeSensorValue(value: number | null | undefined): number {
  return value == null || value === undefined ? NaN : value;
}

/**
 * Sanitizes sensor data array, converting missing temperature values to NaN
 * @param sensorData Array of sensor data points
 * @returns Array with missing temperature values converted to NaN
 */
export function sanitizeSensorDataArray(sensorData: any[]): any[] {
  if (!Array.isArray(sensorData)) return [];

  return sensorData.map((dataPoint) => ({
    ...dataPoint,
    values: {
      ...dataPoint.values,
      living_air_temperature: sanitizeSensorValue(dataPoint.values.living_air_temperature),
    },
  }));
}
